/* Highly Educated: shared behaviour.
   Events other scripts rely on (all dispatched on document):
     overlay:open / overlay:close   any drawer or dialog opened or closed (the scroll story pauses smooth scrolling)
     cart:open                      ask the cart drawer to open (detail: { cart? })
     cart:updated                   cart changed (detail: { cart, sections? })
*/
(function () {
  'use strict';

  var FOCUSABLE = 'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"]),iframe,summary';
  var openCount = 0;

  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function trapFocus(container, e) {
    if (e.key !== 'Tab') return;
    var els = focusables(container);
    if (!els.length) { e.preventDefault(); return; }
    var first = els[0], last = els[els.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function overlayChanged(delta) {
    openCount = Math.max(0, openCount + delta);
    document.body.classList.toggle('has-overlay', openCount > 0);
    document.dispatchEvent(new CustomEvent(delta > 0 ? 'overlay:open' : 'overlay:close', { detail: { open: openCount } }));
  }

  /* <he-drawer id="..."> with .drawer__scrim, .drawer__panel and [data-drawer-close] inside.
     Any element with aria-controls="<id>" and data-drawer-open opens it. */
  class HeDrawer extends HTMLElement {
    connectedCallback() {
      this.classList.add('drawer');
      this.setAttribute('aria-hidden', 'true');
      this.panel = this.querySelector('.drawer__panel');
      if (this.bound) return; this.bound = true;
      this.onKey = this.onKey.bind(this);
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target.classList.contains('drawer__scrim')) this.close();
      });
    }
    /* section reload/unload in the editor (or any removal) while open: release the scroll lock and key handler */
    disconnectedCallback() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKey);
      this.setExpanded(false);
      overlayChanged(-1);
    }
    get isOpen() { return this.classList.contains('is-open'); }
    /* aria-expanded belongs only on real disclosure buttons for this drawer, not on e.g. an add-to-cart button */
    setExpanded(state) {
      var o = this.opener;
      if (o && o.getAttribute && this.id && o.getAttribute('aria-controls') === this.id) o.setAttribute('aria-expanded', state ? 'true' : 'false');
    }
    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      this.setExpanded(true);
      document.addEventListener('keydown', this.onKey);
      overlayChanged(1);
      var target = this.querySelector('[autofocus]') || focusables(this.panel)[0];
      requestAnimationFrame(function () { if (target) target.focus({ preventScroll: true }); });
    }
    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', this.onKey);
      this.setExpanded(false);
      if (this.opener && this.opener.focus && this.opener.isConnected) this.opener.focus({ preventScroll: true });
      overlayChanged(-1);
    }
    onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.close(); }
      else trapFocus(this.panel, e);
    }
  }
  if (!customElements.get('he-drawer')) customElements.define('he-drawer', HeDrawer);

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      var d = document.getElementById(opener.getAttribute('aria-controls'));
      if (d && d.open) { e.preventDefault(); d.open(opener); }
    }
    // close any open header dropdown when clicking elsewhere
    document.querySelectorAll('.nav details[open]').forEach(function (det) {
      if (!det.contains(e.target)) det.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('.nav details[open]').forEach(function (det) {
      det.removeAttribute('open'); var s = det.querySelector('summary'); if (s) s.focus();
    });
  });

  /* header cart count follows cart:updated */
  document.addEventListener('cart:updated', function (e) {
    var cart = e.detail && e.detail.cart; if (!cart) return;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count; el.hidden = cart.item_count === 0;
    });
    var label = cartLabel(cart.item_count);
    document.querySelectorAll('[data-cart-label]').forEach(function (el) {
      el.setAttribute('aria-label', label || el.getAttribute('data-cart-label').replace('[count]', cart.item_count));
    });
  });

  /* Plural-aware "Cart, n items". layout/theme.liquid renders header.cart for count 1 (strings.cartLabel.one)
     and for a sentinel count (strings.cartLabel.other, sentinel in strings.cartLabel.n), so Shopify picks the
     right plural form server side and we only swap the number in. */
  function cartLabel(n) {
    var s = window.theme && window.theme.strings && window.theme.strings.cartLabel;
    if (!s || !s.other) return null;
    if (n === 1 && s.one) return s.one;
    return s.other.split(String(s.n)).join(String(n));
  }

  /* small helpers shared by section scripts */
  window.HE = window.HE || {};
  window.HE.trapFocus = trapFocus;
  window.HE.overlayChanged = overlayChanged;
  window.HE.fetchSections = function (url, ids) {
    var u = new URL(url, location.origin);
    u.searchParams.set('sections', ids.join(','));
    return fetch(u.toString(), { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  };
  window.HE.debounce = function (fn, ms) {
    var t; return function () { var a = arguments, s = this; clearTimeout(t); t = setTimeout(function () { fn.apply(s, a); }, ms); };
  };
  /* Formats cents with the store money format (shop.money_format), like Shopify's money filter.
     Supports amount, amount_no_decimals, amount_with_comma_separator, amount_no_decimals_with_comma_separator,
     amount_with_apostrophe_separator, amount_no_decimals_with_space_separator, amount_with_space_separator,
     amount_with_period_and_space_separator. */
  window.HE.money = function (cents, format) {
    var fmt = format || (window.theme && window.theme.moneyFormat) || '${{amount}}';
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents);
    if (!isFinite(cents)) cents = 0;
    function group(n, decimals, thousands, decimal) {
      var v = (Math.abs(n) / 100).toFixed(decimals), parts = v.split('.');
      var out = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (parts[1] ? decimal + parts[1] : '');
      return (n < 0 ? '-' : '') + out;
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, key) {
      switch (key) {
        case 'amount': return group(cents, 2, ',', '.');
        case 'amount_no_decimals': return group(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return group(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(cents, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return group(cents, 0, ' ', ',');
        case 'amount_with_space_separator': return group(cents, 2, ' ', ',');
        case 'amount_with_period_and_space_separator': return group(cents, 2, ' ', '.');
        default: return group(cents, 2, ',', '.');
      }
    });
  };
})();
